export default class SearchModal extends HTMLElement {
    constructor() {
      super();
  
      this.openButton = this.querySelector('[data-action="open-search"]');
      this.closeButton = this.querySelector('[data-action="close-search"]');
      this.overlay = this.querySelector("[data-search-overlay]");
      this.modal = this.querySelector("[data-search-modal]");
      this.searchInput = this.querySelector("[data-search-input]");
      this.searchForm = this.querySelector("[data-search-form]");
  
      // Results containers
      this.initialContent = this.querySelector("[data-search-initial]");
      this.resultsContainer = this.querySelector("[data-search-results]");
      this.noResultsContainer = this.querySelector("[data-search-no-results]");
      this.loadingContainer = this.querySelector("[data-search-loading]");
      this.resultsGrid = this.querySelector("[data-results-grid]");
      this.resultsCount = this.querySelector("[data-results-count]");
  
      // Currency info
      this.currencyCode = this.dataset.currencyCode;
      this.currencySymbol = this.dataset.currencySymbol;
  
      // Money format
      this.moneyFormat = JSON.parse(this.dataset.moneyFormat || '"{{amount}} kr"');
  
      this.isOpen = false;
      this.searchTimeout = null;
      this.currentSearchTerm = "";
  
      this.init();
    }
  
    init() {
      if (!this.modal) return;
  
      // Open modal
      this.openButton?.addEventListener("click", () => this.openModal());
  
      // Close modal
      this.closeButton?.addEventListener("click", () => this.closeModal());
      this.overlay?.addEventListener("click", () => this.closeModal());
  
      // Handle search input
      this.searchInput?.addEventListener("input", (e) => this.handleSearch(e));
  
      // Prevent form submission
      this.searchForm?.addEventListener("submit", (e) => {
        e.preventDefault();
        this.performSearch(this.searchInput.value);
      });
  
      // Handle keyboard events
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.closeModal();
        }
  
        // Open on Command+K or Ctrl+K
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
          e.preventDefault();
          this.openModal();
        }
      });
    }
  
    handleSearch(e) {
      const searchTerm = e.target.value.trim();
  
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
  
      // Show loading state if term is not empty
      if (searchTerm) {
        this.showLoading();
      } else {
        this.showInitialContent();
        return;
      }
  
      // Debounce search
      this.searchTimeout = setTimeout(() => {
        if (searchTerm !== this.currentSearchTerm) {
          this.performSearch(searchTerm);
        }
      }, 300);
    }
  
    async performSearch(searchTerm) {
      if (!searchTerm) {
        this.showInitialContent();
        return;
      }
  
      this.currentSearchTerm = searchTerm;
  
      try {
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page`
        );
        const data = await response.json();
        const products = data.resources.results.products || [];
        const pages = data.resources.results.pages || [];
  
        this.hideLoading();
  
        if (products.length === 0 && pages.length === 0) {
          this.showNoResults();
          return;
        }
  
        this.displayResults(products, pages);
      } catch (error) {
        console.error("Search error:", error);
        this.hideLoading();
        this.showNoResults();
      }
    }
  
    displayResults(products, pages) {
      // Update results count
      const totalResults = products.length + pages.length;
      this.resultsCount.textContent = `${totalResults} ${totalResults === 1 ? "result" : "results"}`;
  
      // Clear previous results
      this.resultsGrid.innerHTML = "";
  
      // Take only first 6 products and 3 pages for display
      const displayProducts = products.slice(0, 6);
      const displayPages = pages.slice(0, 3);
  
      // Build products HTML
      const productsHTML = displayProducts
        .map(
          (product) => `
        <a href="${product.url}" class="group lg:hover:text-black/60 flex items-center gap-4 duration-200 ease-out">
          <div class="aspect-[4/5] relative bg-light-grey w-20 overflow-hidden">
            <div class="absolute top-0 right-0 bottom-0 left-0 flex items-center justify-center [&>img]:max-h-[80%] [&>img]:max-w-[90%] [&>img]:object-contain [&>img]:object-center">
            ${
              product.featured_image
                ? `
              <img
                src="${product.featured_image.url}"
                alt="${product.featured_image.alt}"
                width="80"
                height="80"
                loading="lazy"
                class="h-full w-full object-contain object-center"
              >
            `
                : ""
            }
            </div>
          </div>
          <div class="flex flex-col gap-[2px] text-sm">
            <h4 class="leading-none font-medium">${product.title}</h4>
            <div class="opacity-50">${this.formatMoney(product.price)}</div>
          </div>
        </a>
      `
        )
        .join("");
  
      // Build pages HTML
      const pagesHTML = displayPages
        .map(
          (page) => `
        <a href="${page.url}" class="group lg:hover:text-black/60  flex items-center gap-4 py-1 duration-200 ease-out">
          <div class="flex flex-col gap-[2px] text-sm w-full">
            <h4 class="leading-none font-medium">${page.title}</h4>
          </div>
        </a>
      `
        )
        .join("");
  
      // Insert results with sections
      this.resultsGrid.innerHTML = `
        ${products.length > 0 ? `
          <div class="grid gap-4 p-4">
            <h3 class="text-sm font-medium mb-2 opacity-50">Products</h3>
            ${productsHTML}
          </div>
        ` : ''}
        ${pages.length > 0 ? `
          <div class="grid gap-4 p-4">
            <h3 class="text-sm font-medium mb-2 opacity-50">Pages</h3>
            ${pagesHTML}
          </div>
        ` : ''}
      `;
  
      // Update search term in hidden input
      const searchTermInput = this.querySelector("[data-search-term]");
      if (searchTermInput) {
        searchTermInput.value = this.currentSearchTerm;
      }
  
      // Update results summary
      const resultsSummary = this.querySelector("[data-results-summary]");
      if (resultsSummary) {
        const summaryText = this.dataset.showingXOfXResults || "Showing {{count}} of {{total}} results";
        const displayedCount = Math.min(6, products.length) + Math.min(3, pages.length);
        resultsSummary.textContent = summaryText
          .replace("{{count}}", displayedCount)
          .replace("{{total}}", totalResults);
      }
  
      // Show results container
      this.initialContent.classList.add("hidden");
      this.noResultsContainer.classList.add("hidden");
      this.resultsContainer.classList.remove("hidden");
    }
  
    formatMoney(money) {
      const price = money / 1;
      return this.moneyFormat.replace("{{amount}}", price.toFixed(0));
    }
  
    showLoading() {
      this.loadingContainer.classList.remove("hidden");
      this.initialContent.classList.add("hidden");
      this.resultsContainer.classList.add("hidden");
      this.noResultsContainer.classList.add("hidden");
    }
  
    hideLoading() {
      this.loadingContainer.classList.add("hidden");
    }
  
    showNoResults() {
      this.initialContent.classList.add("hidden");
      this.resultsContainer.classList.add("hidden");
      this.noResultsContainer.classList.remove("hidden");
    }
  
    showInitialContent() {
      this.initialContent.classList.remove("hidden");
      this.resultsContainer.classList.add("hidden");
      this.noResultsContainer.classList.add("hidden");
      this.loadingContainer.classList.add("hidden");
    }
  
    openModal() {
      this.isOpen = true;
  
      // Show overlay
      this.overlay.classList.remove("hidden");
  
      // Show modal
      this.modal.classList.remove("invisible", "opacity-0");
      this.modal.classList.add("visible", "opacity-100");
  
      const headerGroup = document.getElementById("header-group");
  
      // Only modify header group if it exists
      if (headerGroup) {
        if (window.scrollY > 10) {
          headerGroup.classList.remove("duration-300");
          headerGroup.setAttribute("data-scrolled", "false");
        } else {
          headerGroup.classList.remove("duration-300");
          headerGroup.setAttribute("data-scrolled", "false");
        }
      }
  
      // Reset search state
      this.showInitialContent();
  
      // Focus search input
      setTimeout(() => {
        this.searchInput?.focus();
      }, 50);
  
      // Prevent body scroll
      document.body.style.overflow = "hidden";
    }
  
    closeModal() {
      this.isOpen = false;
  
      // Hide overlay
      this.overlay.classList.add("hidden");
  
      // Hide modal
      this.modal.classList.remove("visible", "opacity-100");
      this.modal.classList.add("invisible", "opacity-0");
  
      const headerGroup = document.getElementById("header-group");
      // Only modify header group if it exists
      if (headerGroup) {
        if (window.scrollY > 10) {
          headerGroup.classList.add("duration-300");
          headerGroup.setAttribute("data-scrolled", "true");
        } else {
          headerGroup.classList.add("duration-300");
          headerGroup.setAttribute("data-scrolled", "false");
        }
      }
  
      // Clear search input and results
      if (this.searchInput) {
        this.searchInput.value = "";
      }
      this.showInitialContent();
  
      // Restore body scroll
      document.body.style.overflow = "";
  
      // Return focus to open button
      this.openButton?.focus();
    }
  }

// Define the custom element
if (!customElements.get('search-modal')) {
  customElements.define('search-modal', SearchModal);
}